/**
 * Página de detalle de una cita
 * @module pages/CRMAppointmentDetail
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSanitizedId } from '@/hooks/useSanitizedId';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { AppointmentDetail } from '@/components/appointments/AppointmentDetail';
import { AppointmentForm } from '@/components/appointments/AppointmentForm';
import { Modal } from '@/components/common/Modal';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DateInput } from '@/components/ui/DateInput';
import { useAppointment, useAppointments } from '@/hooks/useAppointments';
import type { AppointmentUpdate } from '@/types/appointment.types';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { crmService } from '@/services/crmService';
import { ActivityLogBox } from '@/components/CRM/ActivityLogBox';

/** Convierte ISO 8601 (UTC) a formato yyyy-MM-ddTHH:mm en UTC para input datetime-local (evita desfase al guardar). */
function formatScheduledAtForInput(iso: string): string {
  return new Date(iso).toISOString().slice(0, 16);
}

export function CRMAppointmentDetail() {
  const id = useSanitizedId();
  const navigate = useNavigate();
  const { goBack } = useNavigationHistory({ fallbackPath: '/crm/appointments' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showScheduleCallModal, setShowScheduleCallModal] = useState(false);
  const [scheduleCallDate, setScheduleCallDate] = useState('');
  const [scheduleCallNotes, setScheduleCallNotes] = useState('');
  const [savingScheduleCall, setSavingScheduleCall] = useState(false);

  const { data: appointment, isLoading, error } = useAppointment(id);
  const { update, delete: deleteAppointment } = useAppointments();

  const handleEdit = async (data: AppointmentUpdate) => {
    if (!id) return;
    try {
      await update({ id, data });
      setShowEditModal(false);
    } catch (error) {
      console.error('Error actualizando cita:', error);
    }
  };

  const handleSaveSummary = async (summary: string) => {
    if (!id) return;
    await update({ id, data: { summary } });
    // La query se invalida en useAppointments; el detalle se actualizará
  };

  const handleDelete = async () => {
    if (!id) return;
    if (confirm('¿Estás seguro de eliminar esta cita?')) {
      try {
        await deleteAppointment(id);
        navigate('/crm/appointments');
      } catch (error) {
        console.error('Error eliminando cita:', error);
      }
    }
  };

  /** Navegar al contacto con el formulario de llamada abierto (pestaña Llamadas). */
  const handleFollowUpCall = () => {
    if (!appointment?.contact_id) return;
    const params = new URLSearchParams({ openCallForm: '1', tab: 'calls' });
    if (appointment.opportunity_id) params.set('opportunity_id', appointment.opportunity_id);
    navigate(`/crm/contacts/${appointment.contact_id}?${params.toString()}`);
  };

  /** Abrir modal para programar llamada de seguimiento (crea tarea tipo call en el contacto). */
  const handleOpenScheduleCall = () => {
    setScheduleCallDate('');
    setScheduleCallNotes('');
    setShowScheduleCallModal(true);
  };

  const handleScheduleCallSubmit = async () => {
    if (!appointment?.contact_id || !scheduleCallDate.trim()) return;
    setSavingScheduleCall(true);
    try {
      const completeTill = new Date(scheduleCallDate).toISOString();
      const text = scheduleCallNotes.trim()
        ? `Llamada de seguimiento programada: ${scheduleCallNotes.trim()}`
        : 'Llamada de seguimiento programada';
      await crmService.createTask({
        text,
        task_type: 'call',
        complete_till: completeTill,
        entity_id: appointment.contact_id,
        entity_type: 'contacts',
        responsible_user_id: appointment.agent_id,
      });
      setShowScheduleCallModal(false);
      setScheduleCallDate('');
      setScheduleCallNotes('');
    } catch (err) {
      console.error('Error programando llamada de seguimiento:', err);
      alert('No se pudo crear la tarea. Inténtalo de nuevo.');
    } finally {
      setSavingScheduleCall(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <EmptyState
          icon={<CalendarIcon className="w-12 h-12" />}
          title="Cita no encontrada"
          description="La cita que buscas no existe o ha sido eliminada."
          action={
            <Button onClick={goBack}>
              Volver a Citas
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <Button
          onClick={goBack}
          variant="ghost"
          size="sm"
          className="mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Volver a Citas
        </Button>
      </div>

      {/* Detalle de cita */}
      <AppointmentDetail
        appointment={appointment}
        onEdit={() => setShowEditModal(true)}
        onDelete={handleDelete}
        onSaveSummary={handleSaveSummary}
        onFollowUpCall={handleFollowUpCall}
        onScheduleCall={handleOpenScheduleCall}
        hasContact={!!appointment.contact_id}
      />

      {/* Log de actividad */}
      {id && (
        <div className="mt-6">
          <ActivityLogBox entityType="appointment" entityId={id} limit={50} />
        </div>
      )}

      {/* Modal de edición */}
      {showEditModal && (
        <Modal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Editar Cita"
        >
          <AppointmentForm
            onSubmit={handleEdit}
            onCancel={() => setShowEditModal(false)}
            defaultValues={{
              scheduled_at: formatScheduledAtForInput(appointment.scheduled_at),
              duration: appointment.duration,
              opportunity_id: appointment.opportunity_id,
              agent_id: appointment.agent_id != null ? String(appointment.agent_id) : undefined,
              contact_id: appointment.contact_id || undefined,
              delegation_id: appointment.delegation_id ?? undefined,
              modality: appointment.modality ?? undefined,
              location: appointment.location || undefined,
              meeting_url: appointment.meeting_url || undefined,
              notes: appointment.notes || undefined,
              timezone: appointment.timezone,
            }}
            opportunityId={appointment.opportunity_id}
            contactId={appointment.contact_id || undefined}
            agentId={appointment.agent_id != null ? String(appointment.agent_id) : undefined}
          />
        </Modal>
      )}

      {/* Modal programar llamada de seguimiento */}
      {showScheduleCallModal && (
        <Modal
          open={showScheduleCallModal}
          onClose={() => !savingScheduleCall && setShowScheduleCallModal(false)}
          title="Programar llamada de seguimiento"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Se creará una tarea de tipo llamada asociada al contacto de esta cita. Podrás verla en el calendario y en la ficha del contacto.
            </p>
            <div className="space-y-2">
              <Label htmlFor="schedule-call-date">Fecha y hora de la llamada</Label>
              <DateInput
                id="schedule-call-date"
                type="datetime-local"
                value={scheduleCallDate}
                onChange={(e) => setScheduleCallDate(e.target.value)}
                required
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="schedule-call-notes">Notas (opcional)</Label>
              <Textarea
                id="schedule-call-notes"
                value={scheduleCallNotes}
                onChange={(e) => setScheduleCallNotes(e.target.value)}
                placeholder="Ej.: recordar enviar documentación"
                rows={3}
                className="w-full"
              />
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowScheduleCallModal(false)}
                disabled={savingScheduleCall}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleScheduleCallSubmit}
                disabled={savingScheduleCall || !scheduleCallDate.trim()}
              >
                {savingScheduleCall ? 'Guardando…' : 'Programar llamada'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
